import type { JSX, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { isProblem } from '@/lib/api/problem';

/**
 * The "loading / error / loaded" triad every BP-03 screen needs, in one place.
 *
 * `src/apps/staff/AsyncSection.tsx` is the same component for BP-01's settings screens,
 * but BP-00 R5 forbids a feature from importing an app shell — so this is the features'
 * copy, and it is exported from this package's barrel so `features/clients` uses the same
 * one rather than a third. (`src/lib/` would be the natural home; BP-01 owns it.)
 *
 * A failure that is an RFC 9457 problem shows the api's own `title`, which is already a
 * short human-readable summary of the problem *type* (§3.1) — the only error text in the
 * app that is not an i18n key, because it is the server's sentence and not ours.
 */
export function Async<T>({
  pending,
  error,
  data,
  children,
}: {
  pending: boolean;
  error: unknown;
  data: T | undefined;
  children: (value: T) => ReactNode;
}): JSX.Element {
  const { t } = useTranslation('common');

  if (pending) {
    return (
      <p role="status" data-testid="loading" className="text-muted-foreground">
        {t('state.loading')}
      </p>
    );
  }
  if (error !== null && error !== undefined) {
    return (
      <p role="alert" data-testid="load-error" className="rounded bg-status-no-show p-3 text-destructive">
        {isProblem(error) ? error.title : t('state.error')}
      </p>
    );
  }
  return <>{children(data as T)}</>;
}
